(() => {
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];
  const strings = JSON.parse(qs('[data-theme-strings]')?.textContent || '{}');
  const t = (key, vars = {}) => (strings[key] || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => (vars[name] ?? ''));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motion = () => document.documentElement.classList.contains('motion');
  const storage = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch { /* private mode */ } },
    remove(key) { try { sessionStorage.removeItem(key); } catch { /* private mode */ } },
  };

  /* Screen readers: read out short confirmations such as "Added to your cart" */
  const announcer = qs('[data-announcer]');
  const announce = (text) => {
    if (!announcer || !text) return;
    announcer.textContent = '';
    setTimeout(() => { announcer.textContent = text; }, 60);
  };

  /* Keyboard: while the slide-out cart or the phone menu is open, Tab stays inside it */
  const focusables = (root) => qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', root)
    .filter((el) => el.getClientRects().length > 0);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const panel = qs('[data-cart-drawer]:not([hidden]) .cart-drawer__panel') || qs('details[open][data-drawer] .drawer__panel');
    if (!panel) return;
    const items = focusables(panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!panel.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // All eight of Shopify's money placeholders, written the way Liquid's | money writes them: [decimals, thousands
  // separator, decimal mark]. Sweden's krona uses amount_with_space_separator, which this code used to leave as raw
  // text on the kit page (2026-10-24).
  const MONEY_STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };
  function formatMoney(cents, format) {
    const write = ([places, thousands, mark]) => {
      const [whole, fraction] = (cents / 100).toFixed(places).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${mark}${fraction}` : grouped;
    };
    return (format || '${{amount}}')
      .replace(/<[^>]*>/g, '')
      .trim()
      .replace(/\{\{\s*(\w+)\s*\}\}/, (placeholder, name) => (MONEY_STYLES[name] ? write(MONEY_STYLES[name]) : placeholder));
  }

  /* Menu drawer, search and dropdowns: close on Escape and outside click */
  const closables = () => qsa('details[open][data-drawer], details[open][data-header-search], details[open].site-nav__dropdown, details[open][data-locale-picker]');
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closables().forEach((d) => { d.open = false; qs('summary', d)?.focus(); });
  });
  document.addEventListener('click', (e) => {
    closables().forEach((d) => {
      if (d.hasAttribute('data-drawer')) {
        if (e.target === d) d.open = false;
      } else if (!d.contains(e.target)) {
        d.open = false;
      }
    });
  });
  qsa('[data-drawer]').forEach((drawer) => {
    drawer.addEventListener('toggle', () => {
      document.documentElement.style.overflow = drawer.open ? 'hidden' : '';
    });
    qs('[data-drawer-close]', drawer)?.addEventListener('click', () => { drawer.open = false; });
  });
  qsa('[data-locale-picker]').forEach((picker) => {
    picker.addEventListener('toggle', () => { if (picker.open) qs('select', picker)?.focus(); });
    picker.addEventListener('focusout', (e) => { if (e.relatedTarget && !picker.contains(e.relatedTarget)) picker.open = false; });
  });
  /* Country and language pickers: a mouse or touch choice switches at once. With the keyboard, arrow keys only
     move through the list; the Update button (shown then) or Enter confirms, so nobody switches country by accident. */
  document.addEventListener('keydown', (e) => {
    const select = e.target.closest?.('[data-localization-select]');
    if (!select) return;
    select.dataset.keyboard = '1';
    const go = qs('[data-localization-go]', select.form);
    if (go) go.hidden = false;
    if (e.key === 'Enter') { e.preventDefault(); select.form?.submit(); }
  });
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]') && !e.target.dataset.keyboard) e.target.form?.submit();
  });

  /* Footer "Cookie preferences": reopens Shopify's cookie banner where it is active for the visitor */
  const cookieButtons = qsa('[data-cookie-preferences]');
  if (cookieButtons.length) {
    let tries = 0;
    const ready = () => window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function';
    const reveal = () => {
      if (ready()) { cookieButtons.forEach((b) => { b.closest('li').hidden = false; }); return; }
      if (tries++ < 20) setTimeout(reveal, 500);
    };
    reveal();
    cookieButtons.forEach((b) => b.addEventListener('click', () => { if (ready()) window.privacyBanner.showPreferences(); }));
  }

  /* Phones: the gliding row of university logos can be paused */
  qsa('[data-logo-pause]').forEach((button) => {
    button.addEventListener('click', () => {
      const paused = button.closest('.logo-strip').classList.toggle('is-paused');
      button.setAttribute('aria-pressed', String(paused));
    });
  });

  /* Heavy animated images (GIFs) load only when they come near the screen */
  const lazyGifs = qsa('img[data-lazy-src]');
  if (lazyGifs.length) {
    const load = (img) => { img.srcset = img.dataset.lazySrcset || ''; img.src = img.dataset.lazySrc; img.removeAttribute('data-lazy-src'); };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { load(entry.target); io.unobserve(entry.target); }
      }), { rootMargin: '300px 0px' });
      lazyGifs.forEach((img) => io.observe(img));
    } else {
      lazyGifs.forEach(load);
    }
  }

  qsa('[data-header-search]').forEach((search) => {
    search.addEventListener('toggle', () => { if (search.open) qs('input', search)?.focus(); });
  });
  qsa('.site-nav__dropdown').forEach((dropdown) => {
    dropdown.addEventListener('toggle', () => {
      if (dropdown.open) qsa('.site-nav__dropdown[open]').forEach((d) => { if (d !== dropdown) d.open = false; });
    });
    const hover = window.matchMedia('(hover: hover)');
    dropdown.addEventListener('mouseenter', () => { if (hover.matches) dropdown.open = true; });
    dropdown.addEventListener('mouseleave', () => { if (hover.matches) dropdown.open = false; });
    // Hovering already opened it, so a mouse click on the title must not close it again (keyboard Enter still toggles).
    qs('summary', dropdown)?.addEventListener('click', (e) => {
      if (hover.matches && dropdown.open && e.detail > 0) e.preventDefault();
    });
    dropdown.addEventListener('focusout', (e) => {
      if (!dropdown.contains(e.relatedTarget)) dropdown.open = false;
    });
  });

  /* YouTube and TikTok: nothing loads from those services until someone presses play. The play button is then
     replaced by the player itself, which gets the keyboard focus. */
  const swapForPlayer = (button, src, className) => {
    const frame = document.createElement('div');
    frame.className = className;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = button.getAttribute('aria-label') || 'Video';
    iframe.allow = 'accelerometer; autoplay; encrypted-media; fullscreen; gyroscope; picture-in-picture';
    frame.append(iframe);
    button.replaceWith(frame);
    iframe.focus();
  };

  // YouTube previews: full HD where the video has one, otherwise YouTube's standard preview
  document.querySelectorAll('img[data-yt-thumb]').forEach((img) => {
    const fallback = () => {
      img.removeAttribute('srcset');
      img.src = `https://i.ytimg.com/vi/${img.dataset.ytThumb}/hqdefault.jpg`;
    };
    const check = () => { if (img.naturalWidth && img.naturalWidth <= 120) fallback(); };
    if (img.complete) check(); else img.addEventListener('load', check, { once: true });
    img.addEventListener('error', fallback, { once: true });
  });
  document.addEventListener('click', (e) => {
    const video = e.target.closest('.lite-video[data-video-id]');
    if (video) {
      swapForPlayer(video, `https://www.youtube-nocookie.com/embed/${video.dataset.videoId}?autoplay=1&rel=0`, 'lite-video lite-video--playing');
      return;
    }
    const tiktok = e.target.closest('[data-tiktok-id]');
    if (tiktok) {
      swapForPlayer(tiktok, `https://www.tiktok.com/player/v1/${tiktok.dataset.tiktokId}?autoplay=1&description=1&music_info=0&rel=0`, 'tiktok-card__frame');
    }
  });

  /* Reviews: show the first part, expand on request */
  const reviewsBody = qs('[data-reviews-body]');
  const reviewsMore = qs('[data-reviews-more]');
  if (reviewsBody && reviewsMore) {
    const update = () => {
      if (reviewsBody.classList.contains('is-expanded')) return;
      const short = reviewsBody.scrollHeight <= reviewsBody.clientHeight + 4;
      reviewsBody.classList.toggle('is-short', short);
      reviewsMore.hidden = short;
    };
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(reviewsBody.firstElementChild);
    update();
    reviewsMore.addEventListener('click', () => {
      const expanded = reviewsBody.classList.toggle('is-expanded');
      reviewsMore.setAttribute('aria-expanded', String(expanded));
      reviewsMore.textContent = expanded ? t('showFewerReviews') : t('showAllReviews');
      if (!expanded) reviewsBody.scrollIntoView({ block: 'start' });
    });
  }

  /* Bundle prices. A kit sold as "1 KIT / 2-4 KITS / 5+ KITS" must be charged at the bundle that matches
     the number of kits in the cart, e.g. "1 KIT" x 2 becomes "2-4 KITS" x 2, so nobody overpays. */
  const tierOf = (title) => {
    const text = String(title || '');
    let m = text.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (m) return [Number(m[1]), Number(m[2])];
    m = text.match(/(\d+)\s*\+/);
    if (m) return [Number(m[1]), Infinity];
    m = text.match(/^\s*(\d+)\b/);
    return m ? [Number(m[1]), Number(m[1])] : null;
  };
  const productRequests = {};
  const bundleLadder = async (handle) => {
    productRequests[handle] = productRequests[handle] || fetch(`${t('rootUrl').replace(/\/?$/, '/')}products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const data = await productRequests[handle];
    if (!data || data.variants.length < 2) return null;
    const tiers = data.variants.map((v) => ({ id: v.id, title: v.title, available: v.available, price: v.price, range: tierOf(v.title) }));
    if (tiers.some((tier) => !tier.range)) return null;
    tiers.sort((a, b) => a.range[0] - b.range[0]);
    // Only a real bundle ladder counts (1, then 2-4, then 5+: no gaps, open-ended at the top), never pack sizes.
    const isLadder = tiers[0].range[0] === 1 && tiers[tiers.length - 1].range[1] === Infinity
      && tiers.every((tier, i) => i === 0 || tier.range[0] === tiers[i - 1].range[1] + 1);
    return isLadder ? tiers : null;
  };
  const bundleFix = async (cart) => {
    const groups = {};
    cart.items.forEach((item) => { (groups[item.product_id] = groups[item.product_id] || []).push(item); });
    const updates = {};
    const messages = [];
    for (const items of Object.values(groups)) {
      const tiers = await bundleLadder(items[0].handle);
      if (!tiers) continue;
      const lines = items.filter((item) => tiers.some((tier) => tier.id === item.variant_id));
      const total = lines.reduce((sum, item) => sum + item.quantity, 0);
      const target = tiers.filter((tier) => tier.available && total >= tier.range[0] && total <= tier.range[1]).pop();
      if (!target || (lines.length === 1 && lines[0].variant_id === target.id)) continue;
      lines.forEach((item) => { updates[item.variant_id] = 0; });
      updates[target.id] = total;
      const saved = lines.reduce((sum, item) => sum + item.final_line_price, 0) - target.price * total;
      messages.push(saved > 0
        ? t('bundleApplied', { tier: target.title, amount: formatMoney(saved, strings.moneyFormat) })
        : t('bundleUpdated', { tier: target.title, quantity: total }));
    }
    return messages.length ? { updates, message: messages.join(' ') } : null;
  };

  /* Cart drawer */
  const cartDrawer = {
    el: () => qs('[data-cart-drawer]'),
    lastFocus: null,
    open(returnTo) {
      const el = this.el();
      if (!el) return;
      this.lastFocus = returnTo || document.activeElement;
      el.classList.remove('is-closing');
      el.hidden = false;
      if (motion()) {
        el.classList.add('is-opening');
        setTimeout(() => el.classList.remove('is-opening'), 500);
      }
      document.documentElement.style.overflow = 'hidden';
      qs('.cart-drawer__panel', el)?.focus();
    },
    close() {
      const el = this.el();
      if (!el || el.hidden || el.classList.contains('is-closing')) return;
      const finish = () => {
        el.classList.remove('is-closing');
        el.hidden = true;
        document.documentElement.style.overflow = '';
        this.lastFocus?.focus?.();
      };
      if (!motion()) { finish(); return; }
      el.classList.remove('is-opening');
      el.classList.add('is-closing');
      setTimeout(finish, 280);
    },
    render(sections) {
      const html = sections && sections['cart-drawer'];
      const wrapper = qs('#shopify-section-cart-drawer');
      if (!html || !wrapper) return;
      const wasOpen = this.el() && !this.el().hidden;
      wrapper.outerHTML = html;
      if (wasOpen) { this.el().hidden = false; qs('.cart-drawer__panel', this.el())?.focus(); }
    },
    async refreshCount() {
      const cart = await (await fetch(`${t('cartUrl')}.js`)).json();
      qsa('[data-cart-count]').forEach((badge) => {
        if (cart.item_count > (parseInt(badge.textContent, 10) || 0) && motion()) {
          badge.classList.remove('is-bumped');
          void badge.offsetWidth; // restart the pop
          badge.classList.add('is-bumped');
        }
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
        const label = cart.item_count === 1 ? t('cartCountOne') : t('cartCountOther').replace('99999', cart.item_count);
        badge.closest('.cart-link')?.setAttribute('aria-label', label);
      });
    },
    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: 'cart-drawer' }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || t('addError'));
      this.render(json.sections);
      await this.settleBundles();
      this.refreshCount();
      return json;
    },
    // Move kits to the bundle price that matches their number; show and read out what changed.
    async settleBundles() {
      try {
        const fix = await bundleFix(await (await fetch(`${t('cartUrl')}.js`)).json());
        if (!fix) return false;
        const response = await fetch(`${t('cartUpdateUrl')}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: fix.updates, ...(this.el() ? { sections: 'cart-drawer' } : {}) }),
        });
        if (!response.ok) return false;
        const json = await response.json();
        this.render(json.sections);
        const drawer = this.el();
        const items = drawer && qs('.cart-drawer__items', drawer);
        if (items) {
          const notice = document.createElement('p');
          notice.className = 'cart-notice';
          notice.textContent = fix.message;
          items.prepend(notice);
        }
        announce(fix.message);
        return fix.message;
      } catch {
        return false;
      }
    },
  };

  // The Back button can restore a page from before the last "Add to cart" (back/forward cache): refresh the cart.
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted) return;
    if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
    cartDrawer.refreshCount().catch(() => {});
    if (!cartDrawer.el()) return;
    try {
      const response = await fetch(`${t('rootUrl').replace(/\/?$/, '/')}?sections=cart-drawer`);
      if (response.ok) cartDrawer.render(await response.json());
    } catch {
      // the badge above is enough
    }
  });

  // The cart page has no slide-out cart: fix the bundle price, then reload once and show what changed.
  if (document.body.classList.contains('template-cart')) {
    const notice = storage.get('mc-bundle-notice');
    if (notice) {
      storage.remove('mc-bundle-notice');
      const heading = qs('main h1');
      const p = document.createElement('p');
      p.className = 'cart-notice';
      p.textContent = notice;
      heading?.after(p);
      announce(notice);
    }
    cartDrawer.settleBundles().then((message) => {
      const last = Number(storage.get('mc-bundle-reload') || 0);
      if (message && Date.now() - last > 5000) {
        storage.set('mc-bundle-reload', String(Date.now()));
        storage.set('mc-bundle-notice', message);
        window.location.reload();
      }
    });
  }

  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-cart-close]')) { cartDrawer.close(); return; }
    const lineButton = e.target.closest('[data-cart-line]');
    if (lineButton) {
      lineButton.disabled = true;
      const label = lineButton.getAttribute('aria-label');
      const again = label
        ? `[data-cart-line="${lineButton.dataset.cartLine}"][aria-label="${CSS.escape(label)}"]`
        : `.cart-drawer__remove[data-cart-line="${lineButton.dataset.cartLine}"]`;
      await cartDrawer.post(`${t('cartChangeUrl')}.js`, { line: Number(lineButton.dataset.cartLine), quantity: Number(lineButton.dataset.cartQty) }).catch(() => {});
      const control = cartDrawer.el() && qs(again, cartDrawer.el());
      if (control && !control.disabled) control.focus();
      return;
    }
    const addButton = e.target.closest('[data-cart-add]');
    if (addButton) {
      addButton.disabled = true;
      await cartDrawer.post(`${t('cartAddUrl')}.js`, { items: [{ id: Number(addButton.dataset.cartAdd), quantity: 1 }] }).catch(() => { addButton.disabled = false; });
      return;
    }
    const cartLink = e.target.closest('.cart-link');
    if (cartLink && cartDrawer.el()) {
      e.preventDefault();
      cartDrawer.open();
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') cartDrawer.close(); });

  /* Motion: cards below the first screen arrive in a short ripple and photos unveil the first time they come
     into view. Anything already on screen when the page opens is left alone, so nothing ever blinks. */
  if (motion()) {
    // On phones the logos glide in a loop, so the whole strip arrives as one piece there.
    const logos = window.matchMedia('(max-width: 989px)').matches ? '.logo-strip' : '.logo-list:not(.logo-list--clone) > *';
    const items = qsa(`.product-grid > *, .book-grid > *, .testimonials__list > *, .video-list > *, .tiktok-row > *, .article-grid > *, ${logos}, .trust__list > *, .product__badges > *, .story-row, .not-found__kits > *, .book-showcase__list > *`);
    const media = qsa('.image-text__media, .video-block__player, .banner__media, .book-intro__image, .kit-spotlight__media, .edition-spotlight__media');
    const below = (el) => el.getBoundingClientRect().top > window.innerHeight;
    const observer = new IntersectionObserver((entries) => {
      entries.filter((entry) => entry.isIntersecting).forEach((entry, i) => {
        entry.target.style.setProperty('--reveal-delay', `${Math.min(i, 6) * 80}ms`);
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.filter(below).forEach((el) => { el.classList.add('reveal-item'); observer.observe(el); });
    media.filter(below).forEach((el) => { el.classList.add('reveal-media'); observer.observe(el); });
  }

  /* Page not found: if the address was a kit that isn't sold in this country, show the kit that ships here */
  const kitPanel = qs('[data-not-found-kit]');
  if (kitPanel) {
    const handle = (window.location.pathname.split('/products/')[1] || '').split('/')[0];
    const kitHandles = (kitPanel.dataset.kitHandles || '').split(',').filter(Boolean);
    const cards = qsa('[data-kit-handle]', kitPanel);
    if (handle && kitHandles.includes(handle)) {
      cards.forEach((li) => { li.hidden = li.dataset.kitHandle === handle; });
      if (cards.some((li) => !li.hidden)) {
        kitPanel.hidden = false;
        qs('[data-not-found-general]')?.setAttribute('hidden', '');
      }
    }
  }

  /* Shopify's cookie banner and its preferences window (#shopify-pc__...): while one is open, base.css hides the
     Inbox chat button, which otherwise sat on top of "Decline" (declining must be as easy as accepting; 2026-10-18).
     After a choice Shopify keeps the banner with display: none, so check its height; checked twice a second because
     the banner appears a moment after the page and can be reopened from "Cookie preferences" in the footer. */
  const checkCookieBanner = () => {
    const open = [...document.querySelectorAll('[id^="shopify-pc__"]')].some((el) => el.offsetHeight > 50);
    document.documentElement.classList.toggle('cookie-banner-open', open);
  };
  checkCookieBanner();
  setInterval(checkCookieBanner, 500);

  /* Product page */
  const product = qs('[data-product]');
  if (!product) return;
  const data = JSON.parse(qs('[data-product-json]', product).textContent);
  const form = qs('[data-product-form]', product);
  const idInput = qs('[data-variant-id]', product);
  const addButton = qs('[data-add-to-cart]', product);
  const addLabel = qs('[data-add-label]', product);
  const priceNow = qs('[data-price-now]', product);
  const qtyInput = qs('[data-qty-input]', product) || qs('input[name="quantity"]', product);
  const qtyHint = qs('[data-qty-hint]', product);
  const sticky = qs('[data-sticky-buy]');
  const stickyPrice = qs('[data-sticky-price]');
  const stickyAdd = qs('[data-sticky-add]');

  // On phones the photos sit side by side in a row you can swipe; on computers one photo shows at a time.
  const gallery = qs('[data-gallery-track]', product);
  const phoneGallery = window.matchMedia('(max-width: 989px)');
  const showMedia = (mediaId, scroll = true) => {
    if (!mediaId) return;
    let active = null;
    qsa('[data-media-id]', product).forEach((slide) => {
      const on = slide.dataset.mediaId === String(mediaId);
      slide.classList.toggle('is-active', on);
      if (on) active = slide;
    });
    qsa('[data-thumb]', product).forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.thumb === String(mediaId)));
    if (scroll && active && gallery && phoneGallery.matches) {
      gallery.scrollTo({ left: active.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  };
  qsa('[data-thumb]', product).forEach((thumb) => thumb.addEventListener('click', () => showMedia(thumb.dataset.thumb)));
  if (gallery) {
    const syncTabStop = () => { gallery.tabIndex = phoneGallery.matches ? 0 : -1; };
    syncTabStop();
    phoneGallery.addEventListener?.('change', syncTabStop);
    let settle;
    gallery.addEventListener('scroll', () => {
      if (!phoneGallery.matches) return;
      clearTimeout(settle);
      settle = setTimeout(() => {
        const slide = qsa('[data-media-id]', gallery)[Math.round(gallery.scrollLeft / gallery.clientWidth)];
        if (slide) showMedia(slide.dataset.mediaId, false);
      }, 80);
    }, { passive: true });
  }

  const totalLine = qs('[data-total]', product);
  const formError = qs('[data-form-error]', product);
  const radios = qsa('input[type="radio"][name^="bundle-"]', product);
  const money = (cents) => formatMoney(cents, data.moneyFormat);
  const tierRange = (radio) => [parseInt(radio.dataset.minQty, 10) || 1, parseInt(radio.dataset.maxQty, 10) || Infinity];
  const currentVariant = () => data.variants.find((v) => String(v.id) === idInput.value);
  const qty = () => Math.max(1, parseInt(qtyInput?.value, 10) || 1);

  const updateTotal = () => {
    const variant = currentVariant();
    if (!variant) return;
    const q = qty();
    // The phone buy bar shows what the shopper will actually pay for the number of kits chosen.
    if (stickyPrice) stickyPrice.textContent = money(variant.price * q);
    if (!totalLine) return;
    totalLine.hidden = q < 2;
    totalLine.textContent = t('total', { total: money(variant.price * q), quantity: q, price: money(variant.price) });
  };

  const showVariant = (radio) => {
    const variant = data.variants.find((v) => String(v.id) === radio.value);
    if (!variant) return;
    idInput.value = variant.id;
    priceNow.textContent = money(variant.price);
    addButton.disabled = !variant.available;
    if (stickyAdd) stickyAdd.disabled = !variant.available;
    addLabel.textContent = variant.available ? t('addToCart') : t('soldOut');
    showMedia(variant.mediaId);
    if (formError) formError.hidden = true;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  const setHint = (text) => {
    if (!qtyHint) return;
    qtyHint.hidden = !text;
    qtyHint.textContent = text || '';
  };

  let switchedTier = null;
  radios.forEach((radio) => radio.addEventListener('change', () => {
    switchedTier = null;
    showVariant(radio);
    const [min, max] = tierRange(radio);
    if (qtyInput) qtyInput.value = min;
    setHint(min > 1 ? (max === Infinity ? t('tierMin', { min }) : t('tierRange', { min, max })) : '');
    updateTotal();
  }));

  // Typing a quantity moves to the bundle that matches it, so nobody pays the single-kit price for a group order.
  const syncTierToQty = () => {
    if (!qtyInput) return;
    const q = qty();
    qtyInput.value = q;
    if (radios.length) {
      const match = radios
        .filter((r) => !r.disabled)
        .filter((r) => { const [min, max] = tierRange(r); return q >= min && q <= max; })
        .sort((a, b) => tierRange(b)[0] - tierRange(a)[0])[0];
      const checkedRadio = radios.find((r) => r.checked);
      if (match && match !== checkedRadio) {
        match.checked = true;
        showVariant(match);
        const tier = match.closest('.bundle').querySelector('.bundle__title').textContent.trim();
        switchedTier = q > 1 ? tier : null;
        setHint(q > 1 ? t('switched', { tier, quantity: q }) : '');
      } else if (!match) {
        switchedTier = null;
        setHint('');
      } else if (switchedTier && q > 1) {
        setHint(t('switched', { tier: switchedTier, quantity: q }));
      }
    }
    updateTotal();
  };

  qsa('[data-qty-step]', product).forEach((button) => {
    button.addEventListener('click', () => {
      qtyInput.value = qty() + parseInt(button.dataset.qtyStep, 10);
      syncTierToQty();
    });
  });
  qtyInput?.addEventListener('change', syncTierToQty);

  const checked = radios.find((r) => r.checked);
  if (checked) {
    showVariant(checked);
    // Opened on a bundle option (a shared link or a shopping ad): start at that bundle's smallest number of kits,
    // so the price shown is the price charged.
    const [min, max] = tierRange(checked);
    if (qtyInput && qty() < min) {
      qtyInput.value = min;
      setHint(max === Infinity ? t('tierMin', { min }) : t('tierRange', { min, max }));
    }
  }
  updateTotal();

  form?.addEventListener('submit', async (event) => {
    if (!window.fetch) return;
    event.preventDefault();
    syncTierToQty();
    // Remember where the shopper was (main or sticky button) so closing the cart brings them back there.
    const opener = document.activeElement && document.activeElement !== document.body ? document.activeElement : addButton;
    const label = addLabel.textContent;
    addButton.disabled = true;
    addLabel.textContent = t('adding');
    try {
      const body = new FormData(form);
      if (cartDrawer.el()) body.append('sections', 'cart-drawer');
      const response = await fetch(`${t('cartAddUrl')}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.description || json.message || t('addError'));
      if (cartDrawer.el()) {
        cartDrawer.render(json.sections);
        const bundleMessage = await cartDrawer.settleBundles();
        cartDrawer.refreshCount();
        addButton.disabled = false;
        addLabel.textContent = label;
        cartDrawer.open(opener);
        announce([t('addedToCart', { title: qs('.product__title', product)?.textContent.trim() || '' }), bundleMessage].filter(Boolean).join(' '));
      } else {
        window.location.href = t('cartUrl');
      }
    } catch (error) {
      if (formError) {
        formError.textContent = error.message;
        formError.hidden = false;
      }
      addButton.disabled = false;
      addLabel.textContent = label;
    }
  });

  if (sticky && addButton && 'IntersectionObserver' in window) {
    const computer = window.matchMedia('(min-width: 990px)');
    const io = new IntersectionObserver(([entry]) => {
      // Phones: the bar shows whenever the main button is off screen, so buying is always one tap away.
      // Computers: only once the shopper has scrolled past the main button; on a laptop the button can sit just
      // below the first screen, and the bar would cover the bundle prices (Sabrina 2026-10-16).
      const scrolledPast = entry.boundingClientRect.top < 0;
      sticky.hidden = entry.isIntersecting || (computer.matches && !scrolledPast);
      // The Shopify Inbox chat button sits 20px above the screen's bottom edge, so it covered Add to cart (iPhones
      // make the bar taller for the home indicator; Sabrina 2026-10-18). While the bar shows, base.css ends the chat's
      // box at the bar's top edge, which lifts the button above it.
      document.documentElement.classList.toggle('sticky-buy-open', !sticky.hidden);
      if (!sticky.hidden) document.documentElement.style.setProperty('--sticky-buy-h', `${sticky.offsetHeight}px`);
    });
    io.observe(addButton);
    stickyAdd?.addEventListener('click', () => {
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  }
})();
